import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AuditFilters = ({ onFiltersChange, totalRecords, className = '' }) => {
  const [filters, setFilters] = useState({
    dateRange: 'last_7_days',
    userRole: 'all',
    activityType: 'all',
    dataType: 'all',
    verificationStatus: 'all',
    searchQuery: ''
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const dateRangeOptions = [
    { value: 'today', label: 'Today' },
    { value: 'last_7_days', label: 'Last 7 Days' },
    { value: 'last_30_days', label: 'Last 30 Days' },
    { value: 'last_90_days', label: 'Last 90 Days' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const userRoleOptions = [
    { value: 'all', label: 'All Roles' },
    { value: 'qa_manager', label: 'QA Manager' },
    { value: 'lab_technician', label: 'Lab Technician' },
    { value: 'regulatory_officer', label: 'Regulatory Officer' },
    { value: 'pharmacovigilance', label: 'Pharmacovigilance Officer' },
    { value: 'lab_director', label: 'Lab Director' },
    { value: 'compliance_auditor', label: 'Compliance Auditor' }
  ];

  const activityTypeOptions = [
    { value: 'all', label: 'All Activities' },
    { value: 'test_result', label: 'Test Results' },
    { value: 'equipment_calibration', label: 'Equipment Calibration' },
    { value: 'adverse_event', label: 'Adverse Events' },
    { value: 'regulatory_submission', label: 'Regulatory Submissions' },
    { value: 'batch_approval', label: 'Batch Approvals' },
    { value: 'document_update', label: 'Document Updates' },
    { value: 'user_access', label: 'User Access Changes' }
  ];

  const dataTypeOptions = [
    { value: 'all', label: 'All Data Types' },
    { value: 'quality_control', label: 'Quality Control' },
    { value: 'pharmacovigilance', label: 'Pharmacovigilance' },
    { value: 'regulatory', label: 'Regulatory' },
    { value: 'equipment', label: 'Equipment' },
    { value: 'batch_records', label: 'Batch Records' },
    { value: 'user_management', label: 'User Management' }
  ];

  const verificationStatusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'verified', label: 'Blockchain Verified' },
    { value: 'pending', label: 'Pending Verification' },
    { value: 'failed', label: 'Verification Failed' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleReset = () => {
    const resetFilters = {
      dateRange: 'last_7_days',
      userRole: 'all',
      activityType: 'all',
      dataType: 'all',
      verificationStatus: 'all',
      searchQuery: ''
    };
    setFilters(resetFilters);
    onFiltersChange(resetFilters);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters?.userRole !== 'all') count++;
    if (filters?.activityType !== 'all') count++;
    if (filters?.dataType !== 'all') count++;
    if (filters?.verificationStatus !== 'all') count++;
    if (filters?.searchQuery?.trim()) count++;
    return count;
  };

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="Filter" size={20} className="text-primary" />
          <h3 className="font-medium text-card-foreground">Audit Filters</h3>
          {getActiveFilterCount() > 0 && (
            <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full font-medium">
              {getActiveFilterCount()} active
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">
            {totalRecords?.toLocaleString()} records
          </span>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 text-muted-foreground hover:text-foreground transition-colors duration-150"
          >
            <Icon 
              name={isExpanded ? "ChevronUp" : "ChevronDown"} 
              size={16} 
            />
          </button>
        </div>
      </div>
      {/* Search Bar - Always Visible */}
      <div className="p-4 border-b border-border">
        <Input
          type="search"
          placeholder="Search audit trail by user, action, hash, or description..."
          value={filters?.searchQuery}
          onChange={(e) => handleFilterChange('searchQuery', e?.target?.value)}
          className="w-full"
        />
      </div>
      {/* Expanded Filters */}
      {isExpanded && (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Select
              label="Date Range"
              options={dateRangeOptions}
              value={filters?.dateRange}
              onChange={(value) => handleFilterChange('dateRange', value)}
            />

            <Select
              label="User Role"
              options={userRoleOptions}
              value={filters?.userRole}
              onChange={(value) => handleFilterChange('userRole', value)}
            />

            <Select
              label="Activity Type"
              options={activityTypeOptions}
              value={filters?.activityType}
              onChange={(value) => handleFilterChange('activityType', value)}
            />

            <Select
              label="Data Type"
              options={dataTypeOptions}
              value={filters?.dataType}
              onChange={(value) => handleFilterChange('dataType', value)}
            />

            <Select
              label="Verification Status"
              options={verificationStatusOptions}
              value={filters?.verificationStatus}
              onChange={(value) => handleFilterChange('verificationStatus', value)}
            />
          </div>

          {/* Custom Date Range */}
          {filters?.dateRange === 'custom' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border">
              <Input
                type="date"
                label="Start Date"
                value=""
                onChange={() => {}}
              />
              <Input
                type="date"
                label="End Date"
                value=""
                onChange={() => {}}
              />
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={handleReset}
              iconName="RotateCcw"
              iconPosition="left"
            >
              Reset Filters
            </Button>
            
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
              >
                Export Results
              </Button>
              <Button
                variant="default"
                iconName="Search"
                iconPosition="left"
              >
                Apply Filters
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditFilters;